import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Link } from '#app/components/link'
import { TeamLogo } from '#app/components/team-logo'
import { Table, TableBody, TableBodyCell, TableHead, TableHeadCell } from '#app/components/ui/table'
import { Position, RosterStatus } from '#app/constants'
import {
  getEndDate,
  getStartDate,
  isActiveContract,
  isFutureContract,
} from '#app/services/contracts.server'
import { getPlayerAge } from '#app/services/players.server'
import { prisma } from '#app/utils/db.server'
import {
  formatPlayerNameWithCaptaincy,
  formatDate,
  formatContractLength,
  formatContractType,
  formatNationalityWithFlag,
} from '#app/utils/formatters'
import { generateMetaTags } from '#app/utils/misc'

export async function loader({ params }: LoaderFunctionArgs) {
  const player = await prisma.player.findFirst({
    select: {
      name: true,
      birthday: true,
      birthplace: true,
      nationality: true,
      position: true,
      shoots: true,
      number: true,
      captain: true,
      isActive: true,
      contracts: {
        select: {
          id: true,
          signingTeam: true,
          signingGm: true,
          effectiveDate: true,
          terminationDate: true,
          expirationDate: true,
          length: true,
          type: true,
          effectiveFor: true,
        },
      },
      roster: {
        select: {
          rosterStatus: true,
          team: {
            select: {
              id: true,
              name: true,
              teamLogos: { select: { id: true, altText: true }, where: { current: true } },
            },
          },
        },
        where: {
          NOT: { rosterStatus: RosterStatus.FORMER },
        },
      },
      draftPick: {
        select: {
          year: true,
          round: true,
          pickInRound: true,
          overallPick: true,
          currentTeam: {
            select: {
              name: true,
              teamLogos: { select: { id: true, altText: true }, where: { current: true } },
            },
          },
        },
      },
      disciplinaryActions: {
        select: {
          incidentDate: true,
          incidentNote: true,
          suspensionLength: true,
          fineAmount: true,
        },
      },
    },
    where: { id: parseInt(params.playerId!) },
  })

  invariantResponse(player, 'Player not found', { status: 404 })

  const transformContract = (contract: (typeof player.contracts)[0]) => {
    if (contract == null) {
      return
    }

    return {
      id: contract.id,
      signingTeam: contract.signingTeam,
      signingGm: contract.signingGm,
      effectiveDate: getStartDate(contract),
      isActive: isActiveContract(contract),
      endDate: getEndDate(contract),
      type: contract.type,
      length: contract.length,
      terminated: !!contract.terminationDate,
    }
  }

  return json({
    player: {
      name: player.name,
      birthday: player.birthday,
      age: getPlayerAge(player),
      birthplace: player.birthplace,
      nationality: player.nationality,
      position: player.position,
      shoots: player.shoots,
      number: player.number,
      captain: player.captain,
      isActive: player.isActive,
      rosterStatus: player.roster[0]?.rosterStatus,
      team: player.roster[0]?.team,
      contracts: {
        activeContract: player.contracts
          .filter((contract) => isActiveContract(contract))
          .map((contract) => transformContract(contract)),
        futureContracts: player.contracts
          .filter((contract) => isFutureContract(contract))
          .map((contract) => transformContract(contract)),
        pastContracts: player.contracts
          .filter((contract) => !isActiveContract(contract) && !isFutureContract(contract))
          .map((contract) => transformContract(contract)),
      },
      disciplinaryActions: player.disciplinaryActions,
      draftPick: player.draftPick,
    },
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const playerName = data?.player.name

  const title = `${playerName} | CapBuddy`
  const description = `Player profile for ${playerName} on CapBuddy`

  return [...generateMetaTags(title, description), { title: `${playerName} | CapBuddy` }]
}

export default function PlayerPage() {
  const { player } = useLoaderData<typeof loader>()

  const renderContractsSection = (
    contracts: typeof player.contracts.pastContracts,
    heading: string,
  ) => {
    if (contracts?.length < 1) {
      return null
    }

    return (
      <div>
        <h3 className="text-h5 mb-2 mt-6 text-gray-12">{heading}</h3>
        <Table>
          <TableHead>
            <TableHeadCell>Effective Date</TableHeadCell>
            <TableHeadCell>Signed By</TableHeadCell>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell>Length</TableHeadCell>
            <TableHeadCell>End Date</TableHeadCell>
          </TableHead>
          <TableBody>
            {contracts.map((contract) => (
              <tr className="text-body-sm text-center even:bg-gray-2" key={contract.id}>
                <TableBodyCell>{formatDate(new Date(contract.effectiveDate))}</TableBodyCell>
                <TableBodyCell>
                  {contract.signingGm}
                  {' - '}
                  {contract.signingTeam}
                </TableBodyCell>
                <TableBodyCell>{formatContractType(contract)}</TableBodyCell>
                <TableBodyCell>{formatContractLength(contract)}</TableBodyCell>
                <TableBodyCell>{`${formatDate(new Date(contract.endDate))}${contract.terminated ? ' (Terminated)' : ''}`}</TableBodyCell>
              </tr>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  const renderTeamStatus = () => {
    if (!player.isActive) {
      return 'Retired'
    }

    if (player.team == null) {
      return 'Free Agent'
    }

    return (
      <Link to={`/teams/${player.team.id}`}>
        <div className="flex items-center gap-3">
          {player.team.name}
          <TeamLogo logo={player.team.teamLogos[0]} className="inline-block h-8" />
        </div>
      </Link>
    )
  }

  return (
    <main className="container mb-32 max-w-6xl">
      <section>
        <div className="mb-8 border-b border-gray-6 pb-4 text-gray-12">
          <h1 className="text-h1">
            {formatPlayerNameWithCaptaincy(player)}
            {player.number !== 0 && ` #${player.number}`}
          </h1>
          <p className="text-h3 mt-3 font-medium">{renderTeamStatus()}</p>
        </div>
        <div className="text-body grid grid-cols-1 leading-6 sm:grid-cols-3">
          <p className="col-span-1">
            <span className="font-medium text-gray-12">Birthday: </span>
            {formatDate(new Date(player.birthday))}
          </p>
          <p className="col-span-1 sm:col-span-2">
            <span className="font-medium text-gray-12">Birthplace: </span>
            {player.birthplace}
          </p>
          <p className="col-span-1">
            <span className="font-medium text-gray-12">Age: </span>
            {player.age}
          </p>
          <p className="col-span-1 sm:col-span-2">
            <span className="font-medium text-gray-12">Nationality: </span>
            {formatNationalityWithFlag(player)}
          </p>
          <p className="col-span-1">
            <span className="font-medium text-gray-12">Position: </span>
            {`${player.position[0]}${player.position.slice(1).toLowerCase()}`}
          </p>
          <p className="col-span-1 sm:col-span-2">
            <span className="font-medium text-gray-12">
              {player.position === Position.GOALIE ? 'Catches' : 'Shoots'}:{' '}
            </span>
            {`${player.shoots[0]}${player.shoots.slice(1).toLowerCase()}`}
          </p>
          <div className="col-span-1 flex gap-1 sm:col-span-3">
            <span className="font-medium text-gray-12">Drafted: </span>
            <div className="inline">
              {player.draftPick == null ? (
                'Undrafted'
              ) : (
                <>
                  {player.draftPick.year} - Round {player.draftPick.round}, Pick{' '}
                  {player.draftPick.pickInRound} ({player.draftPick.overallPick} overall)
                  <span className="flex items-center gap-2">
                    {player.draftPick.currentTeam.name}
                    <TeamLogo
                      logo={player.draftPick.currentTeam.teamLogos[0]}
                      className="inline-block h-4"
                    />
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className="mt-6">
        <h2 className="text-h2 -mb-3 text-gray-12">Contracts</h2>
        {renderContractsSection(player.contracts.activeContract, 'Active Contract')}
        {renderContractsSection(player.contracts.futureContracts, 'Future Contracts')}
        {renderContractsSection(player.contracts.pastContracts, 'Previous Contracts')}
      </section>
    </main>
  )
}
